import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { Infer } from 'superstruct';

import { addApiRequestInfo } from '@adflow/shared/util-sentry';

import { formatPathParams } from './formatPathParams';
import type { AnyRouteSchema } from './route-schema-types';

type ReqData<S extends AnyRouteSchema> = Infer<S['req']>;

type QueryWithoutAutoAdds<
	S extends AnyRouteSchema,
	O extends AnyRequesterTypeOptions,
> = Omit<ReqData<S>['query'], keyof O['autoAddedQueries']>;

/**
 * The data needed to make this request that is not provided by the axios
 * instance.
 */
type ReqDataManual<
	S extends AnyRouteSchema,
	O extends AnyRequesterTypeOptions,
> = Omit<ReqData<S>, 'query'> &
	(NonNullable<unknown> extends QueryWithoutAutoAdds<S, O>
		? { query?: QueryWithoutAutoAdds<S, O> }
		: { query: QueryWithoutAutoAdds<S, O> });

interface AnyRequesterTypeOptions {
	/**
	 * The paths that this requester can work with. This is used to separate the
	 * API's which may use different Axios instances.
	 */
	pathPattern: string;

	/**
	 * The queries that are added by the Axios instance.
	 */
	autoAddedQueries?: Record<string, unknown>;
}

export function makeRequester<
	RequesterTypeOptions extends AnyRequesterTypeOptions,
>(axiosInstance: AxiosInstance) {
	return async <
		S extends AnyRouteSchema & {
			pathPattern: RequesterTypeOptions['pathPattern'];
		},
	>(
		routeSchema: S,
		reqData: NoInfer<ReqDataManual<S, RequesterTypeOptions>>,
		reqOptions?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'params'>
	) => {
		try {
			const response = await axiosInstance.request<Infer<S['res']>>({
				...reqOptions,
				method: routeSchema.method,
				url: formatPathParams(routeSchema.pathPattern, reqData.params),
				data: reqData.body,
				params: reqData.query,
			});

			return response.data;
		} catch (err) {
			addApiRequestInfo(err, {
				method: routeSchema.method,
				pathPattern: routeSchema.pathPattern,
				params: reqData.params,
				query: reqData.query,
			});
			throw err;
		}
	};
}
